import { memo } from "react";
import { ActivityIndicator, Pressable } from "react-native";
import Animated, { FadeIn, FadeOut, LinearTransition } from "react-native-reanimated";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { PressableScale } from "pressto";
import { View, Text, Card } from "@src/components";
import { Icon } from "@src/components/Icon";
import { AlarmStateBadge } from "./AlarmStateBadge";
import { DiagonalStripeSquare } from "@src/features/schedules/components/DiagonalStripeSquare";
import { formatExceptionDate, formatExceptionTime } from "@src/features/alarms/utils/exception-format";
import {
  CameraThumbnailCard,
  CameraSkeletonCard,
  cameraThumbnailGridStyles,
  CameraGhostCard,
} from "@src/features/cameras/components/CameraThumbnailCard";
import { getCameraKeyString } from "@src/utils/key-parser";
import type { CameraApiResponse } from "@src/features/cameras";
import type { ExceptionParam } from "@src/features/alarms/types/exception-param";

interface ExceptionHeaderTitleProps {
  isNew: boolean;
  isSaving: boolean;
  hasChanges: boolean;
  onSave: () => void;
}

export const ExceptionHeaderTitle = memo<ExceptionHeaderTitleProps>(({ isNew, isSaving, hasChanges, onSave }) => (
  <View flexDirection="row" justifyContent="space-between" alignItems="center" flex={1}>
    <View flex={1}>
      <Text textStyle="md/semibold" color="text" textAlign="center" style={styles.headerTitle}>
        {isNew ? "New exception" : "Edit Exception"}
      </Text>
    </View>
    <PressableScale
      onPress={onSave}
      style={[styles.saveButton, { opacity: !isSaving && hasChanges ? 1 : 0.5 }]}
      enabled={!isSaving && hasChanges}
    >
      {isSaving ?
        <ActivityIndicator size="small" />
      : <Text textStyle="md/medium" color="white">
          Save
        </Text>
      }
    </PressableScale>
  </View>
));

interface InlineSaveButtonProps {
  isSaving: boolean;
  hasChanges: boolean;
  onSave: () => void;
}

export const InlineSaveButton = memo<InlineSaveButtonProps>(({ isSaving, hasChanges, onSave }) => (
  <View alignItems="flex-end" mb="xl">
    <Pressable
      onPress={onSave}
      style={{ opacity: !isSaving && hasChanges ? 1 : 0.5 }}
      disabled={isSaving || !hasChanges}
    >
      {isSaving ?
        <ActivityIndicator size="small" />
      : <Text textStyle="md/semibold" color={hasChanges ? "navy" : "textMuted"}>
          Save
        </Text>
      }
    </Pressable>
  </View>
));

interface ExceptionSummaryCardProps {
  exceptionData: ExceptionParam;
  cameraLabel: string;
  tz: string;
  isSaving: boolean;
  onPress: () => void;
}

export const ExceptionSummaryCard = memo<ExceptionSummaryCardProps>(
  ({ exceptionData, cameraLabel, tz, isSaving, onPress }) => {
    const { theme } = useUnistyles();
    return (
      <PressableScale onPress={onPress} enabled={!isSaving}>
        <Card>
          <View flexDirection="row" justifyContent="space-between" alignItems="flex-start" gap="md">
            <View flex={1}>
              <View flexDirection="row" alignItems="center" gap="md" mb="2xl">
                <View style={{ overflow: "hidden" }}>
                  <DiagonalStripeSquare state={exceptionData.action} size={20} />
                </View>
                <Text textStyle="sm/medium" numberOfLines={2} ellipsizeMode="tail" style={styles.reason}>
                  {exceptionData.reason}
                </Text>
                <AlarmStateBadge state={exceptionData.action} />
              </View>

              <View flexDirection="row" alignItems="center" gap="xs" mb="xs">
                <Icon name="videoOutline" color={theme.colors.textMuted} size={16} />
                <Text textStyle="xs/regular" color="textMuted">
                  {cameraLabel}
                </Text>
              </View>

              <View flexDirection="row" alignItems="center" gap="xs">
                <Icon name="calendarOutline" color={theme.colors.textMuted} size={16} />
                <Text textStyle="xs/regular" color="textMuted">
                  {formatExceptionDate(exceptionData.period, tz)}
                </Text>
                <Text textStyle="xs/regular" color="textMuted">
                  •
                </Text>
                <Icon name="clockOutline" color={theme.colors.textMuted} size={16} />
                <Text textStyle="xs/regular" color="textMuted">
                  {formatExceptionTime(exceptionData.period, tz)}
                </Text>
              </View>
            </View>
            <Icon name="edit" color={theme.colors.icon} size={20} />
          </View>
        </Card>
      </PressableScale>
    );
  },
);

interface CamerasSectionHeaderProps {
  title: string;
  count: number;
  bulkActionLabel: string;
  onBulkAction: () => void;
  isSaving: boolean;
  transitionMs: number;
}

export const CamerasSectionHeader = memo<CamerasSectionHeaderProps>(
  ({ title, count, bulkActionLabel, onBulkAction, isSaving, transitionMs }) => (
    <Animated.View layout={LinearTransition.duration(transitionMs)}>
      <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="3xl">
        <View flexDirection="row" alignItems="center" gap="sm">
          <Text textStyle="md/medium">{title}</Text>
          <Text textStyle="xs/regular" color="textMuted">
            ({count} {count === 1 ? "camera" : "cameras"})
          </Text>
        </View>
        {count > 0 && (
          <Animated.View entering={FadeIn.duration(300)} exiting={FadeOut.duration(300)}>
            <PressableScale onPress={onBulkAction} enabled={!isSaving}>
              <View style={styles.outlinedButton}>
                <Text textStyle="xs/regular">{bulkActionLabel}</Text>
              </View>
            </PressableScale>
          </Animated.View>
        )}
      </View>
    </Animated.View>
  ),
);

export const EmptyIncludedCard = memo<{ transitionMs: number }>(({ transitionMs }) => {
  const { theme } = useUnistyles();
  return (
    <Animated.View
      entering={FadeIn.duration(300)}
      exiting={FadeOut.duration(300)}
      layout={LinearTransition.duration(transitionMs)}
    >
      <Card style={styles.card}>
        <View alignItems="center" py="7xl">
          <Icon name="infoOutline" color={theme.colors.text} size={24} style={{ marginBottom: theme.spacing.xl }} />
          <Text textStyle="sm/medium" mb="xl">
            No cameras
          </Text>
          <Text textStyle="xs/regular" color="text" textAlign="center">
            Select from available cameras below to be included in this exception.
          </Text>
        </View>
      </Card>
    </Animated.View>
  );
});

interface CamerasGridProps {
  primary: CameraApiResponse[];
  skeletonSource: CameraApiResponse[];
  transitioning: Set<string>;
  selected: boolean;
  isSaving: boolean;
  onToggle: (cameraKey: string) => void;
  transitionMs: number;
  skeletonFirst?: boolean;
}

export const CamerasGrid = memo<CamerasGridProps>(
  ({ primary, skeletonSource, transitioning, selected, isSaving, onToggle, transitionMs, skeletonFirst = false }) => {
    const skeletons = skeletonSource
      .filter((c) => transitioning.has(getCameraKeyString(c)))
      .map((camera) => (
        <Animated.View
          key={`skeleton-${getCameraKeyString(camera)}`}
          entering={FadeIn.duration(300)}
          layout={LinearTransition.duration(transitionMs)}
          style={cameraThumbnailGridStyles.gridItem}
        >
          <CameraSkeletonCard />
        </Animated.View>
      ));

    const cards = primary.map((camera) => {
      const key = getCameraKeyString(camera);
      const isGhost = transitioning.has(key);
      return (
        <Animated.View
          key={key}
          exiting={FadeOut.duration(300)}
          layout={LinearTransition.duration(transitionMs)}
          style={cameraThumbnailGridStyles.gridItem}
        >
          {isGhost ?
            <CameraGhostCard />
          : <CameraThumbnailCard
              camera={camera}
              isSelected={selected}
              onToggle={() => onToggle(key)}
              isSaving={isSaving}
            />
          }
        </Animated.View>
      );
    });

    return (
      <Animated.View layout={LinearTransition.duration(transitionMs)} style={cameraThumbnailGridStyles.grid}>
        {skeletonFirst ?
          <>
            {skeletons}
            {cards}
          </>
        : <>
            {cards}
            {skeletons}
          </>
        }
      </Animated.View>
    );
  },
);

export const styles = StyleSheet.create((theme) => ({
  saveButton: {
    paddingHorizontal: theme.spacing["5xl"],
    backgroundColor: theme.colors.interactive.buttons.default_background,
    borderRadius: theme.spacing["6xl"],
    marginRight: theme.spacing["5xl"],
    borderCurve: "continuous",
    height: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  headerTitle: {
    marginLeft: theme.spacing["5xl"],
  },
  outlinedButton: {
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.default_background,
    borderRadius: theme.spacing.md,
    paddingHorizontal: theme.spacing["3xl"],
    paddingVertical: theme.spacing.sm,
  },
  card: {
    backgroundColor: theme.colors.borderCard,
  },
  reason: {
    flexShrink: 1,
  },
}));
